<!--
  =====================================================================
  ARROW UP - SCROLL TO TOP BUTTON
  =====================================================================
  
  Floating button component that appears when user scrolls down the page.
  
  FEATURES:
  - Shows/hides based on scroll position (threshold: 300px)
  - Tracks footer visibility to adjust position
  - Hides automatically when mega menu is present
  - Smooth scroll to top animation
  - Keyboard accessible (Enter, Space)
  
  OBSERVERS:
  - Scroll listener (passive) for performance
  - IntersectionObserver for footer visibility
  - MutationObserver for mega menu presence
  
  =====================================================================
-->

<template>
  <button
    v-if="!isMenuPresent"
    ref="arrowBtn"
    class="arrow-up"
    :class="{ 'is-visible': isVisible, 'in-footer': isInFooter }"
    @click="scrollToTop"
    aria-label="Torna all'inizio"
    @keyup.enter="scrollToTop"
    @keyup.space="scrollToTop"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="icon"
      aria-hidden="true"
    >
      <path d="M7 14L12 9L17 14" />
    </svg>
  </button>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'

// ───────────────────────────────────────────────────────────────────
// REACTIVE STATE
// ───────────────────────────────────────────────────────────────────

/** Determines if button should be visible based on scroll position */
const isVisible = ref(false)

/** Tracks if button is currently inside footer element */
const isInFooter = ref(false)

/** Prevents showing button when mega menu is open */
const isMenuPresent = ref(false)

/** Reference to button element for DOM operations */
const arrowBtn = ref(null)

/** Scroll threshold (px) before button appears */
const THRESHOLD = 300

// ───────────────────────────────────────────────────────────────────
// OBSERVER INSTANCES
// ───────────────────────────────────────────────────────────────────

/** IntersectionObserver for monitoring footer visibility */
let footerObserver

/** MutationObserver for tracking mega menu presence in DOM */
let domObserver

// ───────────────────────────────────────────────────────────────────
// SCROLL & INTERACTION HANDLERS
// ───────────────────────────────────────────────────────────────────

/**
 * Handles scroll events and updates button visibility
 * Called with passive listener for better scroll performance
 */
function handleScroll() {
  isVisible.value = window.scrollY > THRESHOLD
}

/**
 * Smoothly scrolls page to top
 * Uses native scrollTo API with smooth behavior
 */
function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// ───────────────────────────────────────────────────────────────────
// DOM TRACKING
// ───────────────────────────────────────────────────────────────────

/**
 * Updates menu presence flag by checking DOM
 * Called initially and on any DOM mutation
 */
function updateMenuPresence() {
  isMenuPresent.value = !!document.querySelector('.mega-menu')
}

// ───────────────────────────────────────────────────────────────────
// LIFECYCLE HOOKS
// ───────────────────────────────────────────────────────────────────

/**
 * Initialize scroll tracking and observers on mount
 * 
 * Setup:
 * 1. Add scroll listener (passive for performance)
 * 2. Setup IntersectionObserver for footer element
 * 3. Setup MutationObserver for mega menu changes in DOM
 */
onMounted(async () => {
  // 1. Scroll listener - passive for better performance
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll() // Initial check

  // 2. IntersectionObserver for footer visibility
  const footer = document.querySelector('footer')
  if (footer) {
    footerObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isInFooter.value = entry.isIntersecting
        })
      },
      { threshold: 0.1 }
    )
    footerObserver.observe(footer)
  }

  // 3. MutationObserver for mega menu presence
  await nextTick()
  updateMenuPresence()
  domObserver = new MutationObserver(() => updateMenuPresence())
  domObserver.observe(document.body, { childList: true, subtree: true })
})

/**
 * Clean up all event listeners and observers on unmount
 * Prevents memory leaks and event handler accumulation
 */
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  if (footerObserver) footerObserver.disconnect()
  if (domObserver) domObserver.disconnect()
})
</script>

<style lang="scss" scoped>
@import "@/assets/styles/partials/variables";

/* ------------------------------------------------------------------
   Arrow up button - Base styles
------------------------------------------------------------------ */
.arrow-up {
  position: fixed;
  bottom: max(#{$space-1-5}, calc(var(--footer-total-height, 140px) / 6));
  right: $space-1-5;
  padding: $space-0-5;
  min-width: $space-3;
  min-height: $space-3;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: 0;
  color: $clr-brand-dark;
  opacity: 0;
  visibility: hidden;
  transform: translateY(6px);
  transition:
    opacity 0.25s ease,
    visibility 0.25s ease,
    transform 0.2s ease,
    color 0.25s ease;
  z-index: 5000;

  .icon {
    width: 3rem;
    height: 3rem;
    pointer-events: none;
  }
}

/* ------------------------------------------------------------------
   Arrow up button - Visible state
------------------------------------------------------------------ */
.arrow-up.is-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

/* ------------------------------------------------------------------
   Arrow up button - In footer state
------------------------------------------------------------------ */
.arrow-up.in-footer {
  color: $clr-tertiary;
}

/* ------------------------------------------------------------------
   Arrow up button - Responsive styles
------------------------------------------------------------------ */
@media (min-width: 768px) {
  .arrow-up {
    min-width: $space-4;
    min-height: $space-4;

    .icon {
      width: 3.5rem;
      height: 3.5rem;
    }
  }
}
</style>